<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useHead } from '@unhead/vue'
import { useRoute, useRouter } from 'vue-router'
import { staticPageHref } from '@/router/products'
import type { Locale } from '@/i18n'
import { forgotPassword, verifyResetCode, resetPassword } from '@/services/auth'
import { ApiError } from '@/services/http'
import { usePasswordRules } from '@/composables/usePasswordRules'
import AppButton from '@/components/ui/AppButton.vue'
import PasswordRulesChecklist from '@/components/ui/PasswordRulesChecklist.vue'

/** Réinitialisation du mot de passe en 3 étapes :
 *  email (envoi du code) → code (vérification) → nouveau mot de passe → fin. */
type Step = 'email' | 'code' | 'password' | 'done'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()

/** Nom du paramètre de requête porté par le lien reçu par email. */
const PASSWORD_RESET_TOKEN_PARAM = 'password_reset_token'
const tokenFromQuery = typeof route.query[PASSWORD_RESET_TOKEN_PARAM] === 'string'
  ? (route.query[PASSWORD_RESET_TOKEN_PARAM] as string)
  : null

/** Mode « initialisation » : l'utilisateur arrive depuis le lien email (token
 *  dans l'URL). On saute les étapes email/code et on va directement au choix du
 *  mot de passe, avec des libellés dédiés. Sinon, parcours « mot de passe oublié ». */
const isInit = tokenFromQuery !== null

const step = ref<Step>(isInit ? 'password' : 'email')

const pageTitle = computed(() => {
  switch (step.value) {
    case 'code': return t('auth.forgot.code.title')
    case 'password': return isInit ? t('auth.forgot.init.title') : t('auth.forgot.password.title')
    case 'done': return isInit ? t('auth.forgot.init.doneTitle') : t('auth.forgot.done.title')
    default: return t('auth.forgot.email.title')
  }
})
useHead({ title: computed(() => `${pageTitle.value} — MySmerra`) })

/** Lien localisé de retour vers la page de connexion. */
const loginHref = computed(() => staticPageHref('login', locale.value as Locale))

/** Libellés de l'étape mot de passe et de l'écran final : ils diffèrent entre
 *  la réinitialisation (mot de passe oublié) et l'initialisation (activation). */
const passwordTitle = computed(() => isInit ? t('auth.forgot.init.title') : t('auth.forgot.password.title'))
const passwordIntro = computed(() => isInit ? t('auth.forgot.init.intro') : t('auth.forgot.password.intro'))
const passwordSubmit = computed(() => isInit ? t('auth.forgot.init.submit') : t('auth.forgot.password.submit'))
const passwordSubmitting = computed(() => isInit ? t('auth.forgot.init.submitting') : t('auth.forgot.password.submitting'))
const doneTitle = computed(() => isInit ? t('auth.forgot.init.doneTitle') : t('auth.forgot.done.title'))
const doneBody = computed(() => isInit ? t('auth.forgot.init.doneBody') : t('auth.forgot.done.body'))

const email = ref('')
const code = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const showPassword = ref(false)

const resetToken = ref<string | null>(tokenFromQuery)

const emailError = ref<string | null>(null)
const codeError = ref<string | null>(null)
const passwordError = ref<string | null>(null)
const generalError = ref<string | null>(null)
const codeResent = ref(false)
const isLoading = ref(false)

/** RFC 5322 « simplifiée » : suffisant pour un contrôle de saisie côté client. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const trimmedEmail = computed(() => email.value.trim())
const isValidEmail = computed(() => EMAIL_RE.test(trimmedEmail.value))

// Complexité (miroir UserPasswordValidator) + concordance, mutualisées.
const { checklist, allRulesMet, canSubmit: canResetPassword } = usePasswordRules(
  password,
  passwordConfirmation,
)

/* ----- Étape 1 : demande du code par email ----- */
async function submitEmail() {
  if (!trimmedEmail.value) {
    emailError.value = t('auth.forgot.email.errorRequired')
    return
  }
  if (!isValidEmail.value) {
    emailError.value = t('auth.forgot.email.errorInvalid')
    return
  }
  emailError.value = null
  generalError.value = null
  isLoading.value = true
  try {
    // Le back répond toujours 200 (anti-énumération) : on avance vers le code.
    await forgotPassword(trimmedEmail.value)
    step.value = 'code'
  } catch {
    generalError.value = t('auth.forgot.errorNetwork')
  } finally {
    isLoading.value = false
  }
}

/* ----- Étape 2 : vérification du code ----- */
async function submitCode() {
  if (!code.value.trim()) {
    codeError.value = t('auth.forgot.code.errorRequired')
    return
  }
  codeError.value = null
  generalError.value = null
  isLoading.value = true
  try {
    resetToken.value = await verifyResetCode(trimmedEmail.value, code.value.trim())
    step.value = 'password'
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      codeError.value = t('auth.forgot.code.errorInvalid')
    } else {
      generalError.value = t('auth.forgot.errorNetwork')
    }
  } finally {
    isLoading.value = false
  }
}

async function resendCode() {
  if (isLoading.value) return
  codeError.value = null
  generalError.value = null
  codeResent.value = false
  isLoading.value = true
  try {
    await forgotPassword(trimmedEmail.value)
    codeResent.value = true
  } catch {
    generalError.value = t('auth.forgot.errorNetwork')
  } finally {
    isLoading.value = false
  }
}

/* ----- Étape 3 : nouveau mot de passe ----- */
async function submitPassword() {
  if (!password.value) {
    passwordError.value = t('auth.forgot.password.errorRequired')
    return
  }
  if (!allRulesMet.value) {
    passwordError.value = t('auth.forgot.password.errorWeak')
    return
  }
  if (password.value !== passwordConfirmation.value) {
    passwordError.value = t('auth.forgot.password.errorMismatch')
    return
  }
  passwordError.value = null
  generalError.value = null
  if (!resetToken.value) {
    // Token perdu : en mode oubli on repart de l'email ; en mode initialisation
    // il n'y a pas d'étape email, on invite à réutiliser le lien reçu.
    generalError.value = isInit
      ? t('auth.forgot.init.errorTokenExpired')
      : t('auth.forgot.password.errorTokenExpired')
    if (!isInit) step.value = 'email'
    return
  }
  isLoading.value = true
  try {
    await resetPassword(resetToken.value, password.value, passwordConfirmation.value)
    step.value = 'done'
  } catch (err) {
    if (err instanceof ApiError && err.status === 422) {
      // Le mot de passe ne respecte pas les règles de sécurité du back.
      passwordError.value = t('auth.forgot.password.errorInvalid')
    } else if (err instanceof ApiError && err.status === 401) {
      // Token de réinitialisation invalide/expiré.
      resetToken.value = null
      if (isInit) {
        // Pas d'étape email en initialisation : on reste sur l'écran et on
        // invite à redemander un lien.
        generalError.value = t('auth.forgot.init.errorTokenExpired')
      } else {
        code.value = ''
        step.value = 'email'
        generalError.value = t('auth.forgot.password.errorTokenExpired')
      }
    } else {
      generalError.value = t('auth.forgot.errorNetwork')
    }
  } finally {
    isLoading.value = false
  }
}

function goToLogin() {
  router.push(loginHref.value)
}
</script>

<template>
  <main id="main" class="auth" aria-labelledby="auth-title">
    <div class="auth__card">
      <!-- Étape 1 : email -->
      <template v-if="step === 'email'">
        <header class="auth__head">
          <h1 id="auth-title" class="auth__title">{{ t('auth.forgot.email.title') }}</h1>
          <p class="auth__subtitle">{{ t('auth.forgot.email.intro') }}</p>
        </header>

        <form class="auth__form" novalidate @submit.prevent="submitEmail">
          <div class="auth__field">
            <label for="forgot-email">{{ t('auth.forgot.email.label') }}</label>
            <input
              id="forgot-email"
              v-model="email"
              type="email"
              autocomplete="email"
              inputmode="email"
              :placeholder="t('auth.forgot.email.placeholder')"
              :disabled="isLoading"
              :aria-invalid="emailError ? 'true' : undefined"
              :aria-describedby="emailError ? 'forgot-email-error' : undefined"
              @input="emailError = null"
            />
            <p v-if="emailError" id="forgot-email-error" class="auth__error" role="alert">
              {{ emailError }}
            </p>
          </div>

          <p v-if="generalError" class="auth__error auth__error--banner" role="alert">{{ generalError }}</p>

          <AppButton type="submit" variant="dark" size="md" block :ring="!isLoading" :disabled="isLoading">
            {{ isLoading ? t('auth.forgot.email.submitting') : t('auth.forgot.email.submit') }}
          </AppButton>

          <a :href="loginHref" class="auth__back">{{ t('auth.forgot.back') }}</a>
        </form>
      </template>

      <!-- Étape 2 : code -->
      <template v-else-if="step === 'code'">
        <header class="auth__head">
          <h1 id="auth-title" class="auth__title">{{ t('auth.forgot.code.title') }}</h1>
          <p class="auth__subtitle">{{ t('auth.forgot.code.intro', { email: trimmedEmail }) }}</p>
        </header>

        <form class="auth__form" novalidate @submit.prevent="submitCode">
          <div class="auth__field">
            <label for="forgot-code">{{ t('auth.forgot.code.label') }}</label>
            <input
              id="forgot-code"
              v-model="code"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              maxlength="6"
              :placeholder="t('auth.forgot.code.placeholder')"
              :disabled="isLoading"
              :aria-invalid="codeError ? 'true' : undefined"
              :aria-describedby="codeError ? 'forgot-code-error' : undefined"
              @input="codeError = null; codeResent = false"
            />
            <p v-if="codeError" id="forgot-code-error" class="auth__error" role="alert">{{ codeError }}</p>
            <p v-else-if="codeResent" class="auth__notice" role="status">{{ t('auth.forgot.code.resent') }}</p>
          </div>

          <p v-if="generalError" class="auth__error auth__error--banner" role="alert">{{ generalError }}</p>

          <AppButton type="submit" variant="dark" size="md" block :ring="!isLoading" :disabled="isLoading">
            {{ isLoading ? t('auth.forgot.code.submitting') : t('auth.forgot.code.submit') }}
          </AppButton>

          <button type="button" class="auth__link-btn" :disabled="isLoading" @click="resendCode">
            {{ t('auth.forgot.code.resend') }}
          </button>
          <a :href="loginHref" class="auth__back">{{ t('auth.forgot.back') }}</a>
        </form>
      </template>

      <!-- Étape 3 : nouveau mot de passe -->
      <template v-else-if="step === 'password'">
        <header class="auth__head">
          <h1 id="auth-title" class="auth__title">{{ passwordTitle }}</h1>
          <p class="auth__subtitle">{{ passwordIntro }}</p>
        </header>

        <form class="auth__form" novalidate @submit.prevent="submitPassword">
          <div class="auth__field">
            <label for="forgot-password">{{ t('auth.forgot.password.label') }}</label>
            <div class="auth__input-wrap">
              <input
                id="forgot-password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="new-password"
                :placeholder="t('auth.forgot.password.placeholder')"
                :disabled="isLoading"
                :aria-invalid="passwordError ? 'true' : undefined"
                @input="passwordError = null"
              />
              <button
                type="button"
                class="auth__reveal"
                :aria-label="showPassword ? t('auth.forgot.password.hide') : t('auth.forgot.password.show')"
                :aria-pressed="showPassword"
                @click="showPassword = !showPassword"
              >
                <svg v-if="showPassword" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8"/>
                </svg>
                <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                  <path d="M2 12s3.5-7 10-7c2 0 3.8.66 5.3 1.6M22 12s-3.5 7-10 7c-2 0-3.8-.66-5.3-1.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M4 4l16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
          </div>

          <div class="auth__field">
            <label for="forgot-password-confirm">{{ t('auth.forgot.password.confirmLabel') }}</label>
            <input
              id="forgot-password-confirm"
              v-model="passwordConfirmation"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="new-password"
              :placeholder="t('auth.forgot.password.confirmPlaceholder')"
              :disabled="isLoading"
              :aria-invalid="passwordError ? 'true' : undefined"
              :aria-describedby="passwordError ? 'forgot-password-error' : undefined"
              @input="passwordError = null"
            />
            <p v-if="passwordError" id="forgot-password-error" class="auth__error" role="alert">{{ passwordError }}</p>
          </div>

          <PasswordRulesChecklist :checklist="checklist" />

          <p v-if="generalError" class="auth__error auth__error--banner" role="alert">{{ generalError }}</p>

          <AppButton type="submit" variant="dark" size="md" block :ring="canResetPassword && !isLoading" :disabled="isLoading || !canResetPassword">
            {{ isLoading ? passwordSubmitting : passwordSubmit }}
          </AppButton>

          <a :href="loginHref" class="auth__back">{{ t('auth.forgot.back') }}</a>
        </form>
      </template>

      <!-- Étape 4 : succès -->
      <template v-else>
        <div class="auth__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="28" height="28">
            <path d="M20 6 9 17l-5-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <header class="auth__head">
          <h1 id="auth-title" class="auth__title">{{ doneTitle }}</h1>
          <p class="auth__subtitle">{{ doneBody }}</p>
        </header>
        <AppButton variant="dark" size="md" block @click="goToLogin">
          {{ t('auth.forgot.done.cta') }}
        </AppButton>
      </template>
    </div>
  </main>
</template>

<style scoped>
.auth {
  min-height: 60vh;
  display: grid;
  place-items: center;
  padding: clamp(2rem, 6vw, 4rem) var(--layout-gutter);
}
.auth__card {
  width: 100%;
  max-width: 26rem;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 1.5rem;
  padding: clamp(1.5rem, 4vw, 2.5rem);
  background: var(--color-white);
  border-radius: var(--radius-xl);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-md);
}
.auth__icon {
  align-self: center;
  display: grid;
  place-items: center;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background: color-mix(in oklab, var(--color-peach) 18%, white);
  color: var(--primary);
}
.auth__head {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  text-align: center;
}
.auth__title {
  font-size: var(--text-3xl);
  font-weight: 800;
  line-height: var(--leading-tight);
  color: var(--color-chocolate);
}
.auth__subtitle {
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
  color: var(--text-muted);
}

.auth__form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.auth__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.auth__field label {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
}
.auth__field input {
  width: 100%;
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-sm);
  background: var(--bg-soft);
  font-size: var(--text-base);
  transition: border-color var(--dur-fast), background var(--dur-fast);
}
.auth__field input:focus {
  outline: none;
  border-color: var(--primary);
  background: var(--color-white);
}
.auth__field input[aria-invalid='true'] {
  border-color: var(--color-cherry);
}

.auth__input-wrap {
  position: relative;
  display: flex;
}
.auth__input-wrap input {
  padding-right: 3rem;
}
.auth__reveal {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 3rem;
  display: grid;
  place-items: center;
  background: none;
  border: 0;
  color: var(--text-muted);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: color var(--dur-fast) var(--ease-out);
}
.auth__reveal:hover { color: var(--color-chocolate); }
.auth__reveal:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: -2px;
}

.auth__error {
  font-size: var(--text-sm);
  color: var(--color-cherry);
}
.auth__error--banner {
  background: color-mix(in oklab, var(--color-cherry) 8%, white);
  padding: 0.625rem 0.75rem;
  border-radius: var(--radius-sm);
}
.auth__notice {
  font-size: var(--text-sm);
  color: var(--color-chocolate);
}

/* (Le suivi des règles de complexité est rendu par PasswordRulesChecklist.) */
.auth__back {
  align-self: center;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
  text-decoration: underline;
  text-underline-offset: 0.18em;
  transition: color var(--dur-fast);
}
.auth__back:hover { color: var(--color-cherry); }
.auth__link-btn {
  align-self: center;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
  text-decoration: underline;
  text-underline-offset: 0.18em;
  background: none;
  border: 0;
  cursor: pointer;
  padding: 0;
  transition: color var(--dur-fast);
}
.auth__link-btn:hover { color: var(--color-cherry); }
.auth__link-btn:disabled { opacity: 0.6; cursor: default; }
</style>
